import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { sitesApi } from '@verlo/shared/services/api';

/**
 * The site the back office is looking at (Back Office Review, wave B). One switcher, in
 * the header, remembered per browser; any page that reports on a site reads `site` from
 * here instead of drawing its own select. `null` means every site the user can see.
 */
const SiteContext = createContext({ sites: [], site: null, setSite: () => {}, loaded: false });
const KEY = 'verlo.backoffice.site';

export const SiteProvider = ({ children }) => {
  const [sites, setSites] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [site, setSiteState] = useState(() => { try { return localStorage.getItem(KEY) || null; } catch { return null; } });

  // `refresh` is for the Sites page: a site added, renamed or retired there shows in the
  // header's switcher at once, not on the next full load.
  const refresh = useCallback(() => sitesApi.list()
    .then(({ data }) => { setSites(data || []); setLoaded(true); })
    .catch(() => setLoaded(true)), []);
  useEffect(() => { refresh(); }, [refresh]);

  // A remembered site that no longer exists falls back to "all".
  useEffect(() => {
    if (loaded && site && sites.length && !sites.some((s) => s.id === site)) setSiteState(null);
  }, [loaded, site, sites]);

  const setSite = (id) => {
    setSiteState(id || null);
    try { if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY); } catch { /* private mode */ }
  };

  const value = useMemo(() => ({ sites, site, setSite, loaded, refresh, current: sites.find((s) => s.id === site) || null }), [sites, site, loaded, refresh]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);

/**
 * Does this site trade on Verlo? One question, asked in one place — and not "does it have
 * a node", which is a different thing: a full site can have its Pi unplugged for an
 * afternoon without becoming a different product.
 *
 * "This site" is the one picked in the header, or the only one there is. Defaults to true
 * while the site list is loading, for a site with no plan set, and across a mixed estate
 * on "all sites", so nothing is hidden from a venue that bought the whole thing and the
 * nav does not flicker on a cold load.
 */
export const useHasTill = () => {
  const { current, sites, loaded } = useSite();
  if (!loaded) return true;
  // The site this question is about. `current` is null until somebody picks one in the
  // header — and a one-site venue can never pick: `SiteSwitcher` renders a label rather
  // than a control below two sites, and nothing selects one for them. Reading the plan off
  // `current` alone therefore answered "yes, it has a till" for every single-site org,
  // which is every back-office customer there is: they got the six till-shaped pages they
  // cannot use, and lost "Add the floor" — the upsell this plan exists to make.
  const one = current || ((sites || []).length === 1 ? sites[0] : null);
  // A user looking at a mixed estate on "All sites" still gets the full nav. There is no
  // single honest answer there, and hiding a page some of their venues do have is the
  // worse mistake.
  if (!one) return true;
  return one.plan !== 'back_office';
};

/**
 * Which site a new row belongs to — and whether that question has an answer yet.
 *
 * The site-scoped collections (`menu_items`, `menu_sections`, `staff`, `shifts`, `stock`,
 * `wastage`, `stock_takes`, `guests`) stamp a `site_id`, and the server will not guess it:
 * a caller who can see two venues is asked which, rather than having one picked for them
 * and a new staff member filed under the wrong pub.
 *
 * One site, or one picked in the header, and there is nothing to ask. More than one with
 * none picked and there is no honest answer — so `ready` is false and the page says so,
 * instead of firing a request that comes back "pass ?site=<id>" with nowhere to pass it.
 */
export const useAuthoringSite = () => {
  const { site, sites, loaded } = useSite();
  // Optimistic while the site list is still coming, the same call `useHasTill` makes just
  // above: almost every venue is one site, and a control that greys out for the first
  // moment of every page is worse than the rare 400 a two-site owner would get by clicking
  // inside that window — which at least now says something true.
  if (!loaded) return { site: site || null, ready: true, reason: null };
  if (site) return { site, ready: true, reason: null };
  if ((sites || []).length <= 1) return { site: null, ready: true, reason: null };
  return { site: null, ready: false, reason: 'Choose a venue above to add to' };
};

/**
 * A page's site, following the header's switcher. `empty` is what the page calls "every
 * site" ('' or 'all'); the page's own select, where it still has one, moves the header too.
 */
export const useFollowedSite = (empty = '') => {
  const { site, setSite } = useSite();
  const value = site || empty;
  const set = (v) => setSite(v && v !== empty ? v : null);
  return [value, set];
};
